<template>
  <section id="stay" class="stay">
    <!-- タイトル・サブタイトル -->
    <div class="text-wrapper">
      <h1 class="title">{{ $t('page.stay.title') }}</h1>
      <div class="sub-title" v-html="$t('page.stay.sub-title')"></div>
    </div>

    <div class="stay-content">
      <div class="top-content">
        <div class="main-visual">
          <img class="image" src="~/assets/pages/stay/ImageStay.webp" alt="" />
        </div>
      </div>
      <div class="main-content">
        <div class="sub-text">
          <p class="subject" v-html="$t('page.stay.detail')"></p>
          <p
            v-for="(text, index) in $tm('page.stay.texts')"
            v-html="$rt(text)"
            class="text"
            :key="index"
          />
        </div>

        <!-- 予約サイトへ -->
        <div class="reserve-content">
          <button class="button" @click="clickReservedForm">
            {{ $t('page.reserve.reserved-status') }}
          </button>
        </div>

        <div class="background" />
        <div class="mask" />
        <div class="title">{{ $t('page.stay.stay-menu-title') }}</div>
        <!-- カルーセル・画像 -->
        <div class="swiper-content">
          <ClientOnly>
            <swiper-container
              id="swiper-container"
              :slides-per-view="'auto'"
              :centered-slides="true"
              :space-between="192"
              :pagination="{
                clickable: true,
                el: '.swiper-pagination',
                enabled: true,
                type: 'bullets',
                renderBullet: () => {
                  if (isSP) {
                    return `<span class='swiper-pagination-bullet' style='margin: 0 8px;'></span>`;
                  } else {
                    return `<span class='swiper-pagination-bullet' style='margin: 0 8px;'></span>`;
                  }
                }
              }"
              :navigation="{
                enabled: true,
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev'
              }"
              :direction="'horizontal'"
              class="swiper-container"
            >
            </swiper-container>
          </ClientOnly>
          <div class="swiper-column-button">
            <div class="swiper-button-prev">
              <svg class="swiper-navigation-icon" width="11" height="20" viewBox="0 0 11 20">
                <path d="M1.5 1.5 L10 10 L1.5 18.5" fill="none" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="swiper-button-next">
              <svg class="swiper-navigation-icon" width="11" height="20" viewBox="0 0 11 20">
                <path d="M1.5 1.5 L10 10 L1.5 18.5" fill="none" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </div>
          </div>
        </div>
        <div class="swiper-pagination-wrapper">
          <div class="swiper-pagination"></div>
        </div>

        <!-- 施設情報 -->
        <div class="other-content-list">
          <ul
            v-for="(item, index) in $tm('page.stay.other-content-list')"
            :key="index"
            class="other-content-item"
          >
            <li class="other-content-title">{{ $rt(item.title) }}</li>
            <li class="other-content-body" v-html="noBreakText($rt(item.body))" />
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>
<script setup lang="ts">
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import { useMounted, useWindowSize } from "@vueuse/core";
import { getStayList, type stayType } from '@/models/StayList'
import { dynamicImageUrlTransPath } from '@/utils/utility'
import { actionExternalNewTabTransition, externalTransitionType } from "~/models/Utility";
const { locale } = useI18n()

defineEmits<{
  'open-image-detail-modal': [stayData: stayType]
}>()

onMounted(async () => {
  await nextTick()
  createStayList()
})

const createStayList = () => {
  const swiperContainer = document.getElementById('swiper-container')
  if (!swiperContainer) return
  getStayList(locale.value).forEach((value) => {
    const swiperSlide = document.createElement('swiper-slide')
    swiperSlide.className = 'slide-item'

    const imageArea = new Image()
    imageArea.className = 'image-area'
    imageArea.src = dynamicImageUrlTransPath(value.image)
    imageArea.alt = value.alt
    swiperSlide.appendChild(imageArea)

    swiperContainer.appendChild(swiperSlide)
  })
}

// 空白や・-を改行禁止させる
const noBreakText = (text: string) => {
  if (!text) return '';
  return text.replace(/ /g, '\u00A0').replace(/・/g, '・\u2060').replace(/-/g, '-\u2060');
};

const isMounted = useMounted()
// SP表示かどうか
const { width } = useWindowSize()
const isSP = computed(() => {
  if (!isMounted.value) return true
  return width.value <= 767
});
const clickReservedForm = () => {
  const url = externalTransitionType.reserve[locale.value];
  actionExternalNewTabTransition(url);
}
</script>
<style lang="scss" scoped>
@use '@/assets/styles/mixins' as m;
@use '@/assets/styles/variables' as v;
@use '@/assets/styles/util' as u;

#stay {
  width: 100%;
  text-align: center;
  background: v.$common-white-color;
  padding-top: 160px;
  @include m.sp() {
    padding-top: 64px;
  }
}

/* テキスト表示部 */
.text-wrapper {

  /* タイトル */
  .title {
    color: v.$accent-color;
    font-size: 1.125rem;
    font-family: v.$accent-font-family;
    font-weight: v.$font-weight-light;
    margin: 0;
    @include m.sp() {
      font-size: 1rem;
      letter-spacing: 6.4px;
    }
  }

  /* サブタイトル */
  .sub-title {
    color: v.$text-title-color;
    font-size: 1.375rem;
    line-height: 1.5;
    word-break: keep-all;
    @include m.sp() {
      font-size: 1.25rem;
      letter-spacing: 3px;
    }
  }
}

/* メインビジュアル */
.main-visual {
  display: flex;
  gap: 32px;
  margin: 96px auto 128px;
  max-width: calc(1280px + 6%);
  @include m.sp() {
    margin: 64px auto 0;
  }

  /* 各画像 */
  .image {
    object-fit: cover;
  }
}

.stay-content {
  width: 100%;
  aspect-ratio: 1920 / 1500;
  display: flex;
  flex-direction: column;

  .top-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    position: relative;

    .title {
      height: 100%;
      font-size: 64px;
      margin-bottom: -50px;
      text-align: center;
      font-weight: v.$font-weight-regular;
    }
    .sub-title {
      font-size: 30px;
    }
    .stay-image {
      position: relative;
      width: 100%;
      aspect-ratio: 1040 / 600;
      display: flex;
      place-content: center;
      justify-content: right;
    }
  }

  .main-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    position: relative;

    .background {
      position: absolute;
      inset: 0;
      background: v.$bg-accent1-color;
      height: 100%;
      width: 100%;
      z-index: 0;
    }

    .mask {
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: 1;
      background: url(~/assets/pages/common/BgTexture01.webp);
      opacity: 0.08;
      mix-blend-mode: multiply;
    }

    /* bring main content above mask */
    .title,
    .subject,
    .text,
    .swiper-content,
    .swiper-column-button {
      position: relative;
      z-index: 2;
    }

    .title {
      flex-wrap: wrap;
      font-size: 1.125rem;
      height: 100%;
      align-content: center;
      font-family: v.$accent-font-family;
      font-weight: v.$font-weight-light;
      color: v.$text-title-color;
      margin: 128px 0 64px;
      letter-spacing: 7.2px;
      @include m.sp() {
        margin: 72px 0 33px;
      }
    }

    .sub-text {
      padding: 136px 3% 0;
      max-width: calc(1280px + 6%);
      margin: 0 auto;
      height: 100%;
      align-content: center;
      @include m.sp() {
        padding: 64px 16px;
      }

      .subject {
        font-size: 1.5rem;
        line-height: 2.3;
        margin-bottom: 74px;
        word-break: keep-all;
        @include m.sp() {
          font-size: 1.25rem;
          margin-bottom: 0;
        }
      }

      .text {
        font-size: 1rem;
        margin-top: 40px;
        line-height: 2.4;
        word-break: keep-all;
        @include m.sp() {
          font-size: 0.875rem;
          margin-top: 32px;
          letter-spacing: 2.1px;;
        }
      }
    }

    .reserve-content {
      width: 328px;
      height: 48px;
      margin: 64px auto 0;
      z-index: 1;
      @include m.sp() {
        max-width: 272px;
        width: 100%;
        margin: 0 auto;
      }

      .button {
        position: relative;
        color: v.$text-base-color;
        font-size: 1rem;
        width: 328px;
        height: 48px;
        border: 1px solid v.$common-element-color;
      }
      &:hover {
        .button {
          color: v.$common-white-color;
          background: v.$accent-color;
          border: none;
          width: 328px;
          height: 48px;
          @include m.sp() {
            max-width: 272px;
            width: 100%;
          }

          &::after {
            opacity: 1;
            transition: all 0.2s;
            /* じわっとサイズが大きくなるアニメーション */
            transform: translate(-50%, -50%) scale(1.015, 1.08);
          }
        }
      }

      @include m.sp() {
        .button {
          color: v.$common-white-color;
          background: v.$accent-color;
          border: none;
          max-width: 272px;
          width: 100%;
          height: 48px;
        }
      }
    }

    .swiper-content {
      width: 100%;
      display: flex;
      flex-direction: column;

      .swiper-container {
        width: 100%;
        height: fit-content;
        text-align: center;
        font-size: 1.125rem;

        display: flex;
        justify-content: center;
        align-items: center;
      }

      :deep(.slide-item) {
        max-width: 1200px;

        .image-area {
          padding: 0 80px;
          width: 100%;

          @include m.sp() {
            padding: 0 4%;
          }
        }
      }
    }

    .swiper-column-button {
      position: absolute;
      inset: 0;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      width: 100%;
      max-width: 1286px;
      align-items: center;
      justify-content: space-between;
      pointer-events: none;
      @include m.sp() {
        position: relative;
        top: 48px
      }
    }
    .swiper-column-button .swiper-button-prev,
    .swiper-column-button .swiper-button-next {
      pointer-events: auto;
      user-select: none;
    }

    .swiper-pagination-wrapper {
      display: flex;
      justify-content: center;
      margin: 32px 0 95px;
      z-index: 2;
      @include m.sp() {
        margin-top: clamp(24px, 4vw, 30px);
        margin-left: auto;
        margin-right: auto;
        margin-bottom: 66px;
        width: fit-content;
      }
    }

    .other-content-list {
      max-width: 1280px;
      z-index: 1;
      background: v.$common-white-color;
      text-align: left;
      margin: 0 auto 128px;
      padding: 32px;
      width: 100%;
      letter-spacing: 3.2px;
      @include m.sp() {
        padding: 16px;
        margin: 0 auto 64px;
      }

      .other-content-item {
        list-style: none;
        margin: 0 0 0 -40px;
        .other-content-title {
          font-size: 0.875rem;
          line-height: 2.4;
          background: v.$bg-list-color;
          padding: 13px 0 12px 18px;
          @include m.sp() {
            line-height: 1.8;
            padding: 6px 8px;
          }
        }
        .other-content-body {
          font-size: 0.875rem;
          line-height: 2.4;
          padding: 13px 18px 0 ;
          word-break: keep-all;
          @include m.sp() {
            line-height: 1.8;
            padding: 12px 8px;
          }
        }
      }
      :not(:last-child) .other-content-body {
        padding: 13px 18px 12px;
        @include m.sp() {
          padding: 12px 8px;
        }
      }
    }
  }
}

.swiper-content-button-prev {
  z-index: 10;
  position: relative;
  right: 30%;
}
.swiper-content-button-next {
  z-index: 10;
  position: relative;
  left: 28%;
}

.swiper-button-prev.swiper-button-disabled,
.swiper-button-next.swiper-button-disabled {
  transition: 0.3s ease-out;
  opacity: 0;
}

/* 矢印 */
.swiper-button-prev,
.swiper-button-next {
  position: absolute;
  top: 50%;
  height: clamp(30px, 6vw, 70px);
  width: auto;
  background: transparent;
  z-index: 10;
  @include m.sp() {
    height: clamp(40px, 7.8vw, 60px);
    width: auto;
  }
}
.swiper-button-prev {
  left: 30px;
  @include m.sp() {
    left: 20px;
  }
}
.swiper-button-next {
  right: 30px;
  @include m.sp() {
    right: 20px;
  }
}

.swiper-pagination {
  position: relative;
  bottom: auto;
  top: auto;
  left: auto;
  --swiper-pagination-color: #543A39;
  --swiper-pagination-bullet-inactive-color: #EDEAE7;
  --swiper-pagination-bullet-inactive-opacity: 1;
}

// Swiperの矢印を強制的に調整
.swiper-navigation-icon path {
  stroke-width: 0.5px;
  stroke: #543a39;
  stroke-linecap: round;
  stroke-linejoin: round;
}
</style>
